import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { trackRegistration } from '@/lib/tracking';
import { ArrowLeft, Mail, BookOpen } from 'lucide-react';

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') === 'register' ? 'register' : 'login';
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginEmail, register, loginGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        const userData = await loginEmail(email, password);
        toast.success('Benvenuto!');
        navigate(userData.role === 'admin' ? '/admin' : '/', { replace: true });
      } else {
        await register(email, password, name);
        trackRegistration('email');
        toast.success('Registrazione completata!');
        navigate('/', { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore di autenticazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Left panel - decorative */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center relative" style={{ backgroundColor: 'var(--bg-dark)' }}>
        <div className="absolute inset-0 opacity-20" style={{ background: 'radial-gradient(circle at 30% 50%, rgba(37,99,235,0.3), transparent 60%)' }} />
        <div className="text-center px-16 relative z-10">
          <BookOpen className="w-8 h-8 mx-auto mb-6" style={{ color: 'var(--accent-light)' }} />
          <h2 className="font-heading text-4xl tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)', color: '#E2E8F0', fontWeight: 600 }}>
            Quintessenza Edizioni
          </h2>
          <p className="font-body text-sm" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-muted)', fontWeight: 300 }}>
            La tua libreria digitale<br />di riferimento
          </p>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center text-xs mb-8 transition-colors hover:opacity-70"
            style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-body)', textDecoration: 'none' }} data-testid="back-to-store-link">
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Torna al negozio
          </Link>

          <h1 className="font-heading text-3xl tracking-tight mb-2" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-primary)', fontWeight: 600 }}>
            {mode === 'login' ? 'Accedi' : 'Registrati'}
          </h1>
          <p className="font-body text-sm mb-8" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-muted)' }}>
            {mode === 'login' ? 'Inserisci le tue credenziali' : 'Crea il tuo account'}
          </p>

          <Button onClick={loginGoogle} variant="outline" className="w-full mb-6 rounded-sm font-body text-sm"
            style={{ borderColor: 'var(--border-light)', color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }} data-testid="google-sso-btn">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Continua con Google
          </Button>

          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border-light)' }} />
            <span className="font-body text-xs" style={{ color: 'var(--text-muted)' }}>oppure</span>
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border-light)' }} />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <Label className="font-body text-xs" style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }}>Nome</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required
                  className="mt-1 rounded-sm font-body" placeholder="Il tuo nome" data-testid="register-name-input" />
              </div>
            )}
            <div>
              <Label className="font-body text-xs" style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }}>Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="mt-1 rounded-sm font-body" placeholder="email@esempio.it" data-testid="auth-email-input" />
            </div>
            <div>
              <Label className="font-body text-xs" style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }}>Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="mt-1 rounded-sm font-body" placeholder="La tua password" data-testid="auth-password-input" />
            </div>
            <Button type="submit" disabled={loading} data-testid="auth-submit-btn"
              className="w-full rounded-sm font-body font-medium tracking-wide"
              style={{ backgroundColor: 'var(--accent)', color: '#fff', fontFamily: 'var(--font-body)' }}>
              {loading ? 'Caricamento...' : mode === 'login' ? 'Accedi' : 'Registrati'}
            </Button>
          </form>

          <p className="font-body text-xs text-center mt-6" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>
            {mode === 'login' ? 'Non hai un account? ' : 'Hai gia\' un account? '}
            <button onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              className="underline hover:opacity-70 transition-opacity" style={{ color: 'var(--accent)' }}
              data-testid="toggle-auth-mode-btn">
              {mode === 'login' ? 'Registrati' : 'Accedi'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
